/**
 * Server functions for the signed-in person's side of organization access:
 * the claim form, invite links and "My organization". Rules live in
 * `~/lib/server/cco-representatives`.
 *
 * Aliases: claim this organization, accept organization invite, my
 * organizations, representative sidebar
 */

import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { authMiddleware } from "~/lib/auth-middleware";
import {
  accessRequestInputSchema,
  type AcceptInviteResult,
  type ClaimState,
  type MyOrganization,
  type MyOrganizationDetail,
} from "~/lib/shared/cco-representatives";
import { UserFacingError } from "~/lib/shared/user-facing-error";

export interface ClaimForm {
  organization: { name: string; slug: string };
  state: ClaimState;
  email: string | null;
}

/** The claim form for one organization, or null when it is not open. */
export const getClaimFormFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(z.object({ slug: z.string().min(1) }))
  .handler(async ({ data, context }): Promise<ClaimForm | null> => {
    const { findPublishedCcoBySlug, getClaimState } =
      await import("~/lib/server/cco-representatives");
    const cco = await findPublishedCcoBySlug(data.slug);
    if (!cco) return null;
    return {
      organization: { name: cco.name, slug: cco.slug },
      state: await getClaimState({ ccoId: cco.id, userId: context.user.id }),
      email: context.user.email,
    };
  });

export const requestOrganizationAccessFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(accessRequestInputSchema)
  .handler(async ({ data, context }): Promise<ClaimState> => {
    const unavailable = new UserFacingError(
      "Claiming this organization isn't available right now.",
    );
    const { findPublishedCcoBySlug, requestAccess } =
      await import("~/lib/server/cco-representatives");
    const cco = await findPublishedCcoBySlug(data.slug);
    if (!cco) throw unavailable;
    return requestAccess({
      ccoId: cco.id,
      userId: context.user.id,
      roleTitle: data.roleTitle,
      message: data.message,
    });
  });

export const acceptOrganizationInviteFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(z.object({ token: z.string().min(1).max(200) }))
  .handler(async ({ data, context }): Promise<AcceptInviteResult> => {
    const { acceptInvite } = await import("~/lib/server/cco-representatives");
    return acceptInvite({ token: data.token, user: context.user });
  });

export const listMyOrganizationsFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async ({ context }): Promise<MyOrganization[]> => {
    const { listMyOrganizations } =
      await import("~/lib/server/cco-representatives");
    return listMyOrganizations(context.user.id);
  });

export const getMyOrganizationFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(z.object({ slug: z.string().min(1) }))
  .handler(async ({ data, context }): Promise<MyOrganizationDetail | null> => {
    const { getMyOrganization } =
      await import("~/lib/server/cco-representatives");
    return getMyOrganization({ userId: context.user.id, slug: data.slug });
  });

export const myOrganizationsQueryOptions = () =>
  queryOptions({
    queryKey: ["my-organizations"],
    queryFn: () => listMyOrganizationsFn(),
    // Short, so staff adding someone shows up on their next navigation.
    staleTime: 30 * 1000,
  });
